import React, { useEffect, useState } from "react";
import { APP_TARGET } from "@/lib/nativeApp";
import useReducedMotion from "@/hooks/useReducedMotion";

const DONE = "tk_splash_done";
const seen = () => { try { return sessionStorage.getItem(DONE) === "1"; } catch { return true; } };
const frame = (fn) => (typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame(fn) : setTimeout(fn, 16));
const cancelFrame = (id) => (typeof window.cancelAnimationFrame === "function" ? window.cancelAnimationFrame(id) : clearTimeout(id));

/** Toukky's logo is the website's, the white mark on the red badge (owner,
 * 2026-10-26); Toukky Partner's is black, the red mark on an ink badge, as the
 * apps' first logo was (owner, 2026-11-01), so the two apps differ. */
export const splashColors = (target) => (target === "partner"
  ? { badge: "var(--touki-black)", mark: "var(--touki-red)" }
  : { badge: "var(--touki-red)", mark: "#fff" });
const { badge, mark } = splashColors(APP_TARGET);

/**
 * The animated part of an app's launch, once per start. It takes over from
 * the native launch screen with the same picture: the app's logo (above) on
 * white. It waits, still, while the first screen draws under it, then
 * plays: the badge breathes and rises, the plane's tip leaps along its arc,
 * "toukky." comes up under it; then the overlay fades in 200 ms over the
 * first screen and is removed. Fading, it takes no tap. Less motion: never
 * played, the launch screen is enough. Mounted in the apps only.
 */
export default function SplashMark() {
  const reduce = useReducedMotion();
  const [state, setState] = useState(() => (seen() ? "off" : "wait"));
  const playing = state !== "off";

  // Scheduled once and only leaving cancels it: when the fade cancelled its
  // own end, the overlay stayed, invisible, over the whole app and no button
  // answered (owner's phone, 2026-10-25). It starts two frames after the
  // first paint, so a busy start does not swallow the animation.
  useEffect(() => {
    if (!playing) return undefined;
    try { sessionStorage.setItem(DONE, "1"); } catch { /* private storage: plays again next time */ }
    let second = null;
    let fade = null;
    let end = null;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      setState("in");
      fade = setTimeout(() => setState("out"), 1800);
      end = setTimeout(() => setState("off"), 2000);
    };
    const first = frame(() => { second = frame(start); });
    // A start that paints no frame still plays, so the overlay never stays.
    const late = setTimeout(start, 1200);
    return () => { cancelFrame(first); cancelFrame(second); clearTimeout(late); clearTimeout(fade); clearTimeout(end); };
  }, [playing]);

  if (!playing || reduce) return null;
  const fading = state === "out";
  const go = state !== "wait" ? " tk-splash-go" : "";
  return (
    <div aria-hidden="true" data-testid="splash-mark" data-state={state}
      className="fixed inset-0 z-skip grid place-content-center justify-items-center bg-white transition-opacity duration-base"
      style={{ opacity: fading ? 0 : 1, pointerEvents: fading ? "none" : undefined }}>
      {/* The app's logo, as big as on the native launch screen (124 dp). */}
      <svg width="124" height="124" viewBox="0 0 32 32" className={`tk-splash -mt-5${go}`}>
        <rect width="32" height="32" rx="8" fill={badge} />
        <path d="M 8 23 Q 14 8 22.5 11" stroke={mark} strokeWidth="2.8" strokeLinecap="round" fill="none" />
        <circle cx="8" cy="23" r="2.8" fill={mark} />
        <g className="t">
          <path d="M 26.4 6.6 L 21 12 L 22.6 12.4 L 22.8 15.6 L 24.4 13.4 L 27.2 14 Z" fill={mark} />
          <path d="M 26.4 6.6 L 22.6 12.4 L 24 12.7 Z" fill={mark} opacity="0.65" />
        </g>
      </svg>
      <div className={`tk-splash-word mt-5 font-heading text-display-xl font-bold text-touki-black${go}`}>
        toukky<span className="text-touki-red">.</span>
        {APP_TARGET === "partner" ? <span className="ms-2 font-sans text-heading-sm font-bold text-touki-muted">Partner</span> : null}
      </div>
      <style>{`
        .tk-splash.tk-splash-go { animation: tk-splash-lift 800ms var(--tk-ease-out) 100ms both; }
        .tk-splash-go .t { transform-box: fill-box; transform-origin: center; animation: tk-splash-leap 700ms var(--tk-ease-out) 300ms both; }
        .tk-splash-word { opacity: 0; transform: translateY(14px); }
        .tk-splash-word.tk-splash-go { animation: tk-splash-rise 700ms var(--tk-ease-out) 600ms forwards; }
        @keyframes tk-splash-lift { 0% { transform: none; } 40% { transform: scale(1.06); } 100% { transform: translateY(-10px); } }
        @keyframes tk-splash-leap { 0% { transform: none; } 45% { transform: translate(2.2px, -2.2px) scale(1.12); } 100% { transform: none; } }
        @keyframes tk-splash-rise { to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
